import React from "react";
import { MapPin, Phone, Clock, Instagram, Navigation } from "lucide-react";

const MAP_QUERY = encodeURIComponent(
  "R Maart, Watur Road, Mahavir Petrol Pump, Partur 431501"
);
const MAP_EMBED = `https://www.google.com/maps?q=${MAP_QUERY}&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${MAP_QUERY}`;

export default function LocationContact() {
  return (
    <section
      id="location"
      data-testid="location-section"
      className="py-20 sm:py-28 bg-[#FBF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="font-latin text-xs uppercase tracking-[0.3em] text-[#9B8B3E] mb-3">
            Visit Us
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A1813] leading-tight">
            <span className="text-[#9B8B3E] italic">Come visit</span> our store
          </h2>
          <p className="mt-4 text-[#5C5646] text-base sm:text-lg font-latin">
            Right in the heart of Partur — easy to reach, a joy to shop.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Map */}
          <div
            className="lg:col-span-7 relative rounded-3xl overflow-hidden border border-[#E5E1D5] bg-white aspect-[4/3] lg:aspect-auto lg:min-h-[460px]"
            data-testid="map-container"
          >
            <iframe
              title="R Maart Location"
              src={MAP_EMBED}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Contact details */}
          <div className="lg:col-span-5 space-y-4">
            <ContactCard
              icon={MapPin}
              label="Address"
              testid="contact-address"
            >
              <p className="font-display text-xl text-[#1A1813] leading-snug font-semibold">
                Watur Road, In Front of Mahavir Petrol Pump, Near Railway Station
              </p>
              <p className="text-[#5C5646] text-sm mt-1 font-latin">Partur 431501, Maharashtra</p>
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-link"
                className="inline-flex items-center gap-2 mt-4 text-sm font-latin font-medium text-[#9B8B3E] hover:text-[#857635]"
              >
                <Navigation className="w-4 h-4" />
                Get Directions
              </a>
            </ContactCard>

            <div className="grid sm:grid-cols-2 gap-4">
              <ContactCard icon={Phone} label="Phone" testid="contact-phone">
                <a
                  href="tel:7278919919"
                  className="font-display text-xl text-[#1A1813] hover:text-[#9B8B3E] transition-colors font-latin tracking-wide font-semibold"
                >
                  7278919919
                </a>
                <p className="text-[#5C5646] text-xs mt-1 font-latin">
                  Call or WhatsApp us
                </p>
              </ContactCard>

              <ContactCard icon={Clock} label="Hours" testid="contact-hours">
                <p className="font-display text-xl text-[#1A1813] leading-snug font-semibold">
                  9 AM – 10 PM
                </p>
                <p className="text-[#5C5646] text-xs mt-1 font-latin">
                  Open daily
                </p>
              </ContactCard>
            </div>

            <ContactCard
              icon={Instagram}
              label="Social"
              testid="contact-instagram"
            >
              <a
                href="https://instagram.com/rmaartpartur"
                target="_blank"
                rel="noopener noreferrer"
                className="font-latin text-lg text-[#1A1813] hover:text-[#9B8B3E] transition-colors font-medium"
              >
                @rmaartpartur
              </a>
              <p className="text-[#5C5646] text-xs mt-1 font-latin">
                Follow us on Instagram
              </p>
            </ContactCard>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, label, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E5E1D5] rounded-2xl p-6 hover:border-[#9B8B3E]/40 transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#F5EFE0] text-[#9B8B3E] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-latin text-[10px] uppercase tracking-[0.25em] text-[#5C5646] mb-2 font-medium">
            {label}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
